import { ColorFilterProps } from "../../types/imageStudio/images";

export const rgbToHex = (r: number, g: number, b: number) => {
    return "#" + [r, g, b]
        .map((v) => v.toString(16).padStart(2, "0"))
        .join("");
};

const clamp = (value: number, min = 0, max = 255) =>
  Math.max(min, Math.min(max, value));

export function getImageData(
  img: HTMLImageElement,
  canvas: HTMLCanvasElement
): ImageData {
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  ctx.drawImage(img, 0, 0);
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
};

export function applyTemperatureTint(
  imageData: ImageData,
  temperature: number,
  tint: number
) {
  const temp = temperature / 100;
  const tintVal = tint / 100;

  for (let i = 0; i < imageData.data.length; i += 4) {
    imageData.data[i] = clamp(imageData.data[i] + temp * 30);       // Rot
    imageData.data[i + 1] = clamp(imageData.data[i + 1] - tintVal * 20); // Grün
    imageData.data[i + 2] = clamp(imageData.data[i + 2] - temp * 30); // Blau
  }
  return imageData
};

export function applyBrightnessContrast(
  imageData: ImageData,
  brightness: number,
  contrast: number
) {
  const b = brightness * 0.8;
  const c = 1 + contrast / 250;

  for (let i = 0; i < imageData.data.length; i += 4) {
    for (let j = 0; j < 3; j++) {
      const value = imageData.data[i + j];
      imageData.data[i + j] = clamp((value - 128) * c + 128 + b);
    }
  }

  return imageData;
};

export function applyHighlightsShadows(
  imageData: ImageData,
  highlights: number,
  shadows: number
) {
  const h = highlights / 100;
  const s = shadows / 100;

  for (let i = 0; i < imageData.data.length; i += 4) {
    const r = imageData.data[i];
    const g = imageData.data[i + 1];
    const b = imageData.data[i + 2];

    const avg = (r + g + b) / 3;

    const highlightMask = Math.max(0, (avg - 140) / 115);
    const shadowMask = Math.max(0, (140 - avg) / 140);

    const highlightAmount = h * 70 * Math.pow(highlightMask, 0.75);
    const shadowAmount = s * 60 * Math.pow(shadowMask, 0.85);

    imageData.data[i] = clamp(r + highlightAmount + shadowAmount);
    imageData.data[i + 1] = clamp(g + highlightAmount + shadowAmount);
    imageData.data[i + 2] = clamp(b + highlightAmount + shadowAmount);
  }

  return imageData;
};

export function applyWhiteness(imageData: ImageData, whiteness: number) {
  const w = whiteness / 100;

  for (let i = 0; i < imageData.data.length; i += 4) {
    const target = w >= 0 ? 255 : 0;
    const amount = Math.abs(w) * 0.45;

    imageData.data[i] = clamp(imageData.data[i] + (target - imageData.data[i]) * amount);
    imageData.data[i + 1] = clamp(imageData.data[i + 1] + (target - imageData.data[i + 1]) * amount);
    imageData.data[i + 2] = clamp(imageData.data[i + 2] + (target - imageData.data[i + 2]) * amount);
  }

  return imageData;
};

export function applySaturationVibrance(
  imageData: ImageData,
  saturation: number,
  vibrance: number
) {
  const sat = saturation / 100;
  const vib = vibrance / 100;

  for (let i = 0; i < imageData.data.length; i += 4) {
    const r = imageData.data[i];
    const g = imageData.data[i + 1];
    const b = imageData.data[i + 2];
    const avg = (r + g + b) / 3;

    imageData.data[i] = clamp(r + (r - avg) * sat + (r - avg) * vib);
    imageData.data[i + 1] = clamp(g + (g - avg) * sat + (g - avg) * vib);
    imageData.data[i + 2] = clamp(b + (b - avg) * sat + (b - avg) * vib);
  }
  return imageData
};

export function applySharpness(
  canvas: HTMLCanvasElement,
  amount: number
) {
  if (amount === 0) return canvas;

  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  const { width, height } = canvas;

  const src = ctx.getImageData(0, 0, width, height);
  const dst = ctx.createImageData(width, height);

  const strength = Math.max(-100, Math.min(100, amount)) / 100;
  const factor = Math.abs(strength) * 2;

  const data = src.data;
  const out = dst.data;

  const getIndex = (x: number, y: number) => (y * width + x) * 4;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = getIndex(x, y);

      if (x === 0 || y === 0 || x === width - 1 || y === height - 1) {
        out[i] = data[i];
        out[i + 1] = data[i + 1];
        out[i + 2] = data[i + 2];
        out[i + 3] = data[i + 3];
        continue;
      }

      const left = getIndex(x - 1, y);
      const right = getIndex(x + 1, y);
      const top = getIndex(x, y - 1);
      const bottom = getIndex(x, y + 1);

      for (let c = 0; c < 3; c++) {
        const blur =
          (data[left + c] +
            data[right + c] +
            data[top + c] +
            data[bottom + c]) /
          4;

        const original = data[i + c];

        out[i + c] =
          strength > 0
            ? clamp(original + (original - blur) * factor)
            : clamp(original + (blur - original) * factor);
      }

      out[i + 3] = data[i + 3];
    }
  }

  ctx.putImageData(dst, 0, 0);
  return canvas;
};
export function applyClarity(
  canvas: HTMLCanvasElement,
  amount: number
) {
  if (amount === 0) return canvas;

  const ctx = canvas.getContext("2d")!;
  const safeAmount = Math.max(-100, Math.min(100, amount));

  ctx.filter = `contrast(${100 + safeAmount * 0.25}%) saturate(${100 + safeAmount * 0.15}%)`;
  ctx.drawImage(canvas, 0, 0);
  ctx.filter = "none";

  return canvas;
};

export function applyVignette(
  canvas: HTMLCanvasElement,
  strength: number
) {
  if (strength === 0) return canvas;

  const ctx = canvas.getContext("2d")!;
  const { width, height } = canvas;

  const radius = Math.sqrt(width * width + height * height) / 2;

  const gradient = ctx.createRadialGradient(
    width / 2,
    height / 2,
    radius * 0.35,
    width / 2,
    height / 2,
    radius
  );

  const amount = Math.min(Math.abs(strength) / 100, 1) * 0.65;

  const color =
    strength > 0
      ? `rgba(0,0,0,${amount})`
      : `rgba(255,255,255,${amount})`;

  gradient.addColorStop(0, "rgba(0,0,0,0)");
  gradient.addColorStop(1, color);

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  return canvas;
};

export default function applyColorFilters(
  img: HTMLImageElement,
  canvas: HTMLCanvasElement,
  filters: ColorFilterProps
) {
  const ctx = canvas.getContext('2d')!;
  let imageData = getImageData(img, canvas);

  imageData = applyTemperatureTint(imageData, filters.temperature, filters.tint);
  imageData = applyBrightnessContrast(imageData, filters.brightness, filters.contrast);
  imageData = applyHighlightsShadows(imageData, filters.highlights, filters.shadow);
  imageData = applyWhiteness(imageData, filters.whiteness);
  imageData = applySaturationVibrance(imageData, filters.saturation, filters.vibrance);

  ctx.putImageData(imageData, 0, 0);

  applySharpness(canvas, filters.sharpness);
  applyClarity(canvas, filters.clarity);
  applyVignette(canvas, filters.vignetting);

  return canvas;
};

export type RotateCanvasOptions = {
  //if true: will create bounding box around canvas and increase image size, if false: canvas will be cropped to original image size
    expandCanvas?: boolean; 
    // only will be considered if expand false
    //if true will increase image to avoid transparent areas in canvas
    coverCanvas?: boolean; 
};

export const rotateCanvas = (canvas: HTMLCanvasElement,degrees: number, options: RotateCanvasOptions = {}) => {
    const { expandCanvas = false, coverCanvas = false} = options;
    const sourceWidth = canvas.width;
    const sourceHeight = canvas.height;

    if (sourceWidth <= 0 || sourceHeight <= 0 ) return;

    const normalizedDegrees = ((degrees % 360) + 360) % 360;

    if (normalizedDegrees === 0) return;

    const radians = normalizedDegrees * Math.PI / 180;

    //keep current canvas
    const sourceCanvas = document.createElement("canvas");
    sourceCanvas.width = sourceWidth;
    sourceCanvas.height = sourceHeight;
    const sourceContext = sourceCanvas.getContext("2d");

    if (!sourceContext) return;

    sourceContext.clearRect(0, 0, sourceWidth, sourceHeight);
    sourceContext.drawImage(canvas, 0, 0);
    const absoluteSin = Math.abs(Math.sin(radians));
    const absoluteCos = Math.abs(Math.cos(radians));

    //if active will increase image size to fit rotated image and fill area transparent
    if (expandCanvas) {
        const targetWidth = Math.round( sourceWidth * absoluteCos + sourceHeight * absoluteSin);
        const targetHeight = Math.round( sourceWidth * absoluteSin + sourceHeight * absoluteCos);
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const targetContext = canvas.getContext("2d");

        if (!targetContext) return;
        
        targetContext.clearRect(0, 0, targetWidth, targetHeight);
        targetContext.save();
        targetContext.translate(targetWidth / 2, targetHeight / 2);
        targetContext.rotate(radians);
        targetContext.drawImage(sourceCanvas, -sourceWidth / 2, -sourceHeight / 2);
        targetContext.restore();
        return;
    }

    canvas.width = sourceWidth;
    canvas.height = sourceHeight;
    const targetContext =canvas.getContext("2d");

    if (!targetContext) return;

    targetContext.clearRect(0, 0, sourceWidth, sourceHeight);
    let drawScale = 1;

    //if active will increase image size by keeping canvas size constandto fit rotated image and avoid transparent corners
    if (coverCanvas) {
        const requiredSourceWidth =
            sourceWidth * absoluteCos +
            sourceHeight * absoluteSin;

        const requiredSourceHeight =
            sourceWidth * absoluteSin +
            sourceHeight * absoluteCos;

        drawScale = Math.max(
            requiredSourceWidth / sourceWidth,
            requiredSourceHeight / sourceHeight
        );
    }

    targetContext.save();

    //rotation point
    targetContext.translate(sourceWidth / 2,sourceHeight / 2);
    targetContext.rotate(radians);
    targetContext.scale( drawScale, drawScale);
    targetContext.drawImage(sourceCanvas,-sourceWidth / 2,-sourceHeight / 2);
    targetContext.restore();
};

export const flipCanvas = (
  canvas: HTMLCanvasElement,
  horizontal: boolean,
  vertical: boolean
) => {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const temp = document.createElement("canvas");
  temp.width = canvas.width;
  temp.height = canvas.height;

  const tctx = temp.getContext("2d");
  if (!tctx) return;

  tctx.clearRect(0, 0, temp.width, temp.height);
  tctx.drawImage(canvas, 0, 0);

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  ctx.save();
  ctx.translate(horizontal ? canvas.width : 0, vertical ? canvas.height : 0);
  ctx.scale(horizontal ? -1 : 1, vertical ? -1 : 1);
  ctx.drawImage(temp, 0, 0);
  ctx.restore();
};
export const getVisibleCanvasCrop = (
    canvas: HTMLCanvasElement,
    wrapper: HTMLDivElement,
    zoom: number,
    panOffset: { x: number; y: number }
) => {
    const wrapperWidth = wrapper.clientWidth;
    const wrapperHeight = wrapper.clientHeight;

    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    const scaledWidth = canvasWidth * zoom;
    const scaledHeight = canvasHeight * zoom;

    // Canvas ist im Wrapper zentriert, dann verschoben
    const canvasLeft = wrapperWidth / 2 - scaledWidth / 2 + panOffset.x;
    const canvasTop = wrapperHeight / 2 - scaledHeight / 2 + panOffset.y;

    const canvasRight = canvasLeft + scaledWidth;
    const canvasBottom = canvasTop + scaledHeight;

    // Sichtbarer Bereich = Schnittmenge aus Wrapper und transformiertem Canvas
    const visibleLeft = Math.max(0, canvasLeft);
    const visibleTop = Math.max(0, canvasTop);
    const visibleRight = Math.min(wrapperWidth, canvasRight);
    const visibleBottom = Math.min(wrapperHeight, canvasBottom);

    const visibleWidth = visibleRight - visibleLeft;
    const visibleHeight = visibleBottom - visibleTop;

    if (visibleWidth <= 0 || visibleHeight <= 0) {
        return null;
    }

    // zurück in unskalierte Canvas-Koordinaten umrechnen
    const cropX = (visibleLeft - canvasLeft) / zoom;
    const cropY = (visibleTop - canvasTop) / zoom;
    const cropW = visibleWidth / zoom;
    const cropH = visibleHeight / zoom;

    return {
        x: Math.max(0, Math.round(cropX)),
        y: Math.max(0, Math.round(cropY)),
        w: Math.min(canvasWidth - Math.round(cropX), Math.round(cropW)),
        h: Math.min(canvasHeight - Math.round(cropY), Math.round(cropH))
    };
};

export const cropVisibleCanvasArea = (
    canvas: HTMLCanvasElement,
    wrapper: HTMLDivElement,
    zoom: number,
    panOffset: { x: number; y: number }
) => {
    const crop = getVisibleCanvasCrop(canvas, wrapper, zoom, panOffset);
    if (!crop) return;

    cropCanvas(canvas, crop);
};

export const cropCanvas = (
  canvas: HTMLCanvasElement,
  crop: { x: number; y: number; w: number; h: number }
) => {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const temp = document.createElement("canvas");
  temp.width = crop.w;
  temp.height = crop.h;

  const tctx = temp.getContext("2d");
  if (!tctx) return;

  tctx.clearRect(0, 0, crop.w, crop.h);
  tctx.drawImage(
    canvas,
    crop.x,
    crop.y,
    crop.w,
    crop.h,
    0,
    0,
    crop.w,
    crop.h
  );

  canvas.width = crop.w;
  canvas.height = crop.h;

  const newCtx = canvas.getContext("2d");
  if (!newCtx) return;

  newCtx.clearRect(0, 0, crop.w, crop.h);
  newCtx.drawImage(temp, 0, 0);
};

export const normalizeRotation = (degrees: number,snap = false,tolerance = 5) => {
    let rotation = ((degrees % 360) + 360) % 360;

    if (!snap) {
        return rotation;
    }
    const targets = [0, 90, 180, 270];

    for (const target of targets) {
        const distance = Math.min(
            Math.abs(rotation - target),
            360 - Math.abs(rotation - target)
        );

        if (distance <= tolerance) {
            return target;
        }
    }

    return rotation;
};

export const applyAspectRatio = (canvas: HTMLCanvasElement, ratio: number) => {
    const w = canvas.width;
    const h = canvas.height;

    let newW = w;
    let newH = w / ratio;

    if (newH > h) {
        newH = h;
        newW = h * ratio;
    }

    const x = (w - newW) / 2;
    const y = (h - newH) / 2;

    cropCanvas(canvas, {
        x,
        y,
        w: newW,
        h: newH
    });
};
export const getImageDimensions = (file: File): Promise<{
  width: number;
  height: number;
}> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
      URL.revokeObjectURL(url);
    };

    img.onerror = reject;

    img.src = url;
  });
};

// makes specific colors in image transparent with transition in tolerance area
export const applySelectedColorTransparencyTransition = (
    canvas: HTMLCanvasElement,
    transparency: { color: string; alpha: number; tolerance?: number } | null
) => {
    if (!transparency) return;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    const { color, alpha, tolerance=40 } = transparency;

    // 0 = unvisible , 1 = visibile
    const visibility = Math.max(0, Math.min(1, alpha));

    const safeTolerance = Math.max(0, tolerance);

    const hex = color.replace("#", "");
    const normalized = hex.length === 3
        ? hex.split("").map(c => c + c).join("")
        : hex;

    const targetR = parseInt(normalized.slice(0, 2), 16);
    const targetG = parseInt(normalized.slice(2, 4), 16);
    const targetB = parseInt(normalized.slice(4, 6), 16);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
        const currentAlpha = data[i + 3];
        if (currentAlpha === 0) continue;

        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];

        const distance = Math.sqrt(
            Math.pow(r - targetR, 2) +
            Math.pow(g - targetG, 2) +
            Math.pow(b - targetB, 2)
        );

        // filter similar colors in tolerance range
        if (distance <= safeTolerance) {
            let closeness = 1;

            if (safeTolerance > 0) {
                closeness = 1 - distance / safeTolerance;
            } else {
                closeness =
                    r === targetR && g === targetG && b === targetB ? 1 : 0;
            }

            const alphaFactor = visibility + (1 - visibility) * (1 - closeness);

            data[i + 3] = Math.round(currentAlpha * alphaFactor);
        }
    }

    ctx.putImageData(imageData, 0, 0);
};

// makes specific colors in image transparent without transition in tolerance area
export const applySelectedColorTransparency = (
    canvas: HTMLCanvasElement,
    transparency: { color: string; alpha: number; tolerance?: number } | null
) => {
    if (!transparency) return;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    const { color, alpha, tolerance=40 } = transparency;

    // 0 = unvisible , 1 = visibile
    const visibility = Math.max(0, Math.min(1, alpha));

    const safeTolerance = Math.max(0, tolerance);

    const hex = color.replace("#", "");
    const normalized = hex.length === 3
        ? hex.split("").map(c => c + c).join("")
        : hex;

    const targetR = parseInt(normalized.slice(0, 2), 16);
    const targetG = parseInt(normalized.slice(2, 4), 16);
    const targetB = parseInt(normalized.slice(4, 6), 16);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
        const currentAlpha = data[i + 3];
        if (currentAlpha === 0) continue;

        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];

        const distance = Math.sqrt(
            Math.pow(r - targetR, 2) +
            Math.pow(g - targetG, 2) +
            Math.pow(b - targetB, 2)
        );

        // filter similar colors in tolerance range
        if (distance <= safeTolerance) {
          data[i + 3] = Math.round(currentAlpha * visibility);
        }
    }

    ctx.putImageData(imageData, 0, 0);
};

// makes entire image transparent in a selected color
export const applyTransparencyToImage = (
    canvas: HTMLCanvasElement,
    transparency: { color: string; alpha: number } | null
) => {
    if (!transparency) return;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    const { color, alpha } = transparency;
    const a = Math.max(0, Math.min(1, alpha));
    if (a <= 0) return;

    const hex = color.replace("#", "");
    const normalized = hex.length === 3
        ? hex.split("").map(c => c + c).join("")
        : hex;

    const rTint = parseInt(normalized.slice(0, 2), 16);
    const gTint = parseInt(normalized.slice(2, 4), 16);
    const bTint = parseInt(normalized.slice(4, 6), 16);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
        const pixelAlpha = data[i + 3];
        if (pixelAlpha === 0) continue; // transparente Pixel unverändert lassen

        data[i]     = Math.round(data[i]     * (1 - a) + rTint * a);
        data[i + 1] = Math.round(data[i + 1] * (1 - a) + gTint * a);
        data[i + 2] = Math.round(data[i + 2] * (1 - a) + bTint * a);
    }

    ctx.putImageData(imageData, 0, 0);
};

